import React, { useState } from "react";
import { ArrowLeft, Check, Copy } from "lucide-react";
import { Spinner, copyTextToClipboard } from "../constants";
import { effectiveConfidence, isEmptyValue } from "./studioConst";
import { SX, FRAME } from "./studioTokens";
import FieldEditor from "./workspace/FieldEditor";

const Stat = ({ n, label, color }) => (
  <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold" style={{ color }}>
    <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />{n} {label}
  </span>
);

/* Post-generate review: the AI's proposed schema alongside what it actually
   pulled out of the sample. Unchecking here disables the field on the draft
   before it is ever saved, so the user commits only to fields they want. */
const GenerateReview = ({ draft, templateName, onNameChange, onToggleField, onSave, onStartOver, saving }) => {
  const fields = draft.fields || [];
  const results = draft.extraction_results || {};
  const [copied, setCopied] = useState(false);

  const selected = fields.filter((f) => f.enabled !== false);
  const populated = selected.filter((f) => !isEmptyValue(results[f.field_key])).length;
  const emptyCount = selected.length - populated;

  const json = JSON.stringify(
    Object.fromEntries(selected.map((f) => [f.field_key, results[f.field_key] ?? null])),
    null, 2
  );
  const copyJson = () => { copyTextToClipboard(json).catch(() => {}); setCopied(true); setTimeout(() => setCopied(false), 1400); };

  const allSelected = selected.length > 0;
  const toggleAll = () => fields.forEach((f) => {
    const want = !allSelected;
    if ((f.enabled !== false) !== want) onToggleField(f.field_key, want);
  });

  const sampleFor = (f) => {
    const v = results[f.field_key];
    if (f.data_type === "table") return `${Array.isArray(v) ? v.length : 0} rows extracted`;
    return isEmptyValue(v) ? "" : (typeof v === "object" ? JSON.stringify(v) : String(v));
  };

  return (
    <div className={`flex flex-col flex-1 min-h-0 font-outfit ${FRAME.canvas}`}>
      {/* Top bar */}
      <div className={`${SX.topbar} gap-3.5`}>
        <button onClick={onStartOver} className={`${SX.btn} ${SX.btnGhost}`}>
          <ArrowLeft size={16} /> Back
        </button>
        <div className={`w-px h-6 ${SX.divider}`} />
        <div className="min-w-0">
          <div className="text-[16px] font-semibold text-[#1A1A1A]">Review AI-generated template</div>
          <div className="text-[12px] text-[#8A8A90]">Verify the fields and extracted values before saving</div>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* Left — fields */}
        <div className="flex-1 min-w-0 overflow-auto px-6 py-[22px]">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-semibold tracking-[0.06em] text-[#8A8A90] uppercase">Suggested Fields</div>
            <button onClick={toggleAll} className="text-[12px] font-semibold text-[#A04A97] hover:text-[#893F82]">
              {allSelected ? "Deselect all" : "Select all"}
            </button>
          </div>
          <div className={`${SX.card} overflow-hidden`}>
            {fields.map((f, i) => (
              <FieldEditor
                key={f.field_key}
                index={i}
                field={f}
                checkbox
                checked={f.enabled !== false}
                onToggle={() => onToggleField(f.field_key, f.enabled === false)}
                confidence={effectiveConfidence(f)}
                sample={sampleFor(f)}
                context={draft}
              />
            ))}
          </div>
          <div className="text-[12px] text-[#8A8A90] mt-3 leading-[1.5]">
            {selected.length} of {fields.length} fields selected — uncheck any you don't need, or add and edit fields after saving.
          </div>
        </div>

        {/* Right — extraction output */}
        <div className="flex-shrink-0 border-l border-[#ECE9EE] bg-white flex flex-col min-h-0"
          style={{ width: "clamp(300px, 34%, 400px)" }}>
          <div className="flex items-center justify-between px-[22px] pt-[18px] pb-3">
            <div className="text-[11px] font-semibold tracking-[0.06em] text-[#8A8A90] uppercase">Extraction Output</div>
            <span className="text-[11px] text-[#8A8A90]">from your sample</span>
          </div>
          <div className="flex items-center gap-4 px-[22px] pb-3.5">
            <Stat n={populated} label="populated" color="#2F7D44" />
            <Stat n={emptyCount} label="empty" color={emptyCount > 0 ? "#C77A1C" : "#8A8A90"} />
            <button onClick={copyJson} className={`${SX.btn} ${SX.btnGhost} ${SX.btnSm} ml-auto`}>
              {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
          </div>
          <div className="flex-1 overflow-auto px-[22px] pb-[22px]">
            <pre className="m-0 p-[18px] bg-[#1A1A1A] text-[#E9D2E6] rounded-[12px] text-[11.5px] leading-[1.7] font-mono whitespace-pre-wrap break-words">{json}</pre>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-4 px-6 py-3.5 border-t border-[#ECE9EE] bg-white flex-shrink-0">
        <button onClick={onStartOver} className="flex items-center gap-1.5 text-[13px] font-semibold text-[#A04A97] hover:text-[#893F82]">
          <ArrowLeft size={14} /> Start over
        </button>
        <div className="flex items-center gap-3">
          <input value={templateName} onChange={(e) => onNameChange(e.target.value)} placeholder="Name this template"
            className={`${SX.input} !w-[260px]`} />
          <button onClick={onSave} disabled={selected.length === 0 || !templateName.trim() || saving}
            className={`${SX.btn} !px-5 !py-2.5 !text-[13.5px] ${selected.length && templateName.trim() && !saving ? SX.btnPrimary : "bg-[#F5F5F5] text-[#8A8A90] border-none cursor-not-allowed"}`}>
            {saving ? <><Spinner size={15} /> Saving…</> : <><Check size={15} /> Save template</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateReview;
